import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "../../../../components/common/Theme/ThemeContext";
import { mcpResourcesService, type MCPResourceRow } from "../MCPResources/mcpResourcesService";
import { developerAccessService, type IssuedTokenRow } from "../Developers/developerAccessService";

// ── helpers ──────────────────────────────────────────────────────────
const GRADIENTS = [
  "from-violet-600 to-indigo-500",
  "from-orange-600 to-yellow-500",
  "from-teal-600 to-blue-500",
  "from-purple-600 to-pink-500",
  "from-orange-500 to-red-500",
  "from-teal-500 to-emerald-400",
  "from-zinc-600 to-zinc-400",
];
const BAR_COLORS = ["bg-indigo-500", "bg-violet-500", "bg-emerald-600", "bg-pink-500", "bg-amber-500", "bg-cyan-500", "bg-rose-500"];

function initFromName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function isThisWeek(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  return d >= startOfWeek;
}

// ── Sparkline ────────────────────────────────────────────────────────
const SPARK = Array.from({ length: 40 }, () => Math.floor(Math.random() * 80 + 20));

function Sparkline() {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const svg = ref.current; if (!svg) return;
    const max = Math.max(...SPARK);
    const pts = SPARK.map((v, i) => `${(i / (SPARK.length - 1)) * 300},${50 - (v / max) * 44}`).join(" ");
    svg.innerHTML = `
      <defs><linearGradient id="dg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#6366f1" stop-opacity=".4"/>
        <stop offset="100%" stop-color="#6366f1" stop-opacity="0"/>
      </linearGradient></defs>
      <polygon points="${pts} 300,50 0,50" fill="url(#dg)"/>
      <polyline points="${pts}" fill="none" stroke="#6366f1" stroke-width="1.5" stroke-linejoin="round"/>
      <circle cx="300" cy="${50 - (SPARK[SPARK.length - 1] / max) * 44}" r="3" fill="#6366f1"/>`;
  }, []);
  return <svg ref={ref} viewBox="0 0 300 50" preserveAspectRatio="none" className="w-full h-12 block" />;
}

// ── StatCard ─────────────────────────────────────────────────────────
function StatCard({ label, value, note, up, loading }: { label: string; value: string; note: string; up?: boolean; loading?: boolean }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded p-4">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-semibold text-white font-mono">{loading ? "—" : value}</p>
      <p className={`text-xs mt-1 ${up ? "text-green-400" : "text-gray-500"}`}>{loading ? "loading…" : note}</p>
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────
export default function MCPDashboard() {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(true);

  const [resources, setResources] = useState<MCPResourceRow[]>([]);
  const [tokens, setTokens] = useState<IssuedTokenRow[]>([]);
  const [devCount, setDevCount] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 2000);
    return () => clearInterval(t);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resList, tokenList, users] = await Promise.all([
        mcpResourcesService.getAll(),
        developerAccessService.getIssuedTokens(),
        developerAccessService.getUsers(),
      ]);
      setResources(resList);
      setTokens(tokenList);
      setDevCount(users.length);
    } catch {
      // silent — cards show "—"
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── computed stats ─────────────────────────────────────────────
  const totalResources = resources.length;
  const activeTokens = tokens.filter(t => t.isActive).length;
  const tokensThisWeek = tokens.filter(t => isThisWeek(t.issued)).length;
  const recentTokens = tokens.slice(0, 5);

  const topResources = resources
    .map((r, i) => ({
      label: r.resourceId || r.name,
      name: r.name,
      bar: BAR_COLORS[i % BAR_COLORS.length],
    }))
    .slice(0, 5);

  const rpm    = 120 + Math.floor((tick * 17 + 33) % 50);
  const latMs  = 70  + Math.floor((tick * 13 + 22) % 40);
  const errPct = (0.1 + (tick * 0.07) % 0.9).toFixed(1);

  return (
    <div className="portal-content p-6 space-y-6 text-sm text-gray-300">

      {/* ── header ──────────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-white">MCP Dashboard</h2>
          <p className="text-sm text-gray-400">Overview of MCP access, usage, and activity.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-green-400 font-mono">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            LIVE
          </span>
          <button className="flex items-center gap-1.5 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-gray-300 px-3 py-1.5 rounded text-xs transition-colors">
            ⬇ Export
          </button>
        </div>
      </div>

      {/* ── stat cards (dynamic) ──────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Resources" value={String(totalResources)} note="registered tools" loading={loading} />
        <StatCard label="Active Tokens"   value={String(activeTokens)}   note={tokensThisWeek > 0 ? `↑ ${tokensThisWeek} this week` : "no new this week"} up={tokensThisWeek > 0} loading={loading} />
        <StatCard label="Developers"      value={String(devCount)}       note="registered users" loading={loading} />
        <StatCard label="Calls / 24h"     value="—" note="coming soon" />
      </div>

      {/* ── mid row ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4">

        {/* recent token issuance (dynamic) */}
        <div className="overflow-x-auto border border-neutral-800 rounded">
          <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800">
            <span className="font-semibold text-white text-sm">Recent Token Issuance</span>
            <span className="text-xs text-gray-500 font-mono">{tokens.length} total</span>
          </div>
          <table className="min-w-full text-sm text-left text-gray-300">
            <thead className="bg-neutral-900 text-gray-400">
              <tr>
                {["Developer", "Role", "Env", "Issued"].map(h => (
                  <th key={h} className="px-4 py-2 text-xs font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500 text-xs">Loading…</td></tr>
              ) : recentTokens.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500 text-xs">No tokens issued yet</td></tr>
              ) : (
                recentTokens.map((t) => (
                  <tr key={t.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded bg-gradient-to-br ${t.grad} flex items-center justify-center text-[9px] font-bold flex-shrink-0`}>{t.init}</div>
                        <span className="text-gray-200">{t.dev}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${isLight ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-indigo-900/50 text-indigo-300 border border-indigo-700/30'}`}>
                        {t.role || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${t.isActive ? "bg-green-700 text-green-100" : "bg-neutral-700 text-gray-300"}`}>
                        {t.env}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{timeAgo(t.issued)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* top MCP resources (dynamic) */}
        <div className="border border-neutral-800 rounded overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800">
            <span className="font-semibold text-white text-sm">Top MCP Resources</span>
            <span className="text-xs text-gray-500 font-mono">{resources.length} total</span>
          </div>
          <div className="p-4 space-y-3 bg-neutral-900">
            {loading ? (
              <p className="text-xs text-gray-500 text-center py-4">Loading…</p>
            ) : topResources.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-4">No resources registered</p>
            ) : (
              topResources.map((d, i) => (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-gray-300 font-mono">{d.label}</span>
                    <span className="text-gray-500">{d.name}</span>
                  </div>
                  <div className="h-1.5 bg-neutral-800 rounded-full">
                    <div className={`h-full ${d.bar} rounded-full`} style={{ width: `${Math.max(30, 100 - i * 15)}%` }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── bottom row ──────────────────────────────────────────── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 290px" }}>

        {/* activity feed (placeholder — no usage tracking API yet) */}
        <div className="border border-neutral-800 rounded overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800">
            <div>
              <span className="font-semibold text-white text-sm">Recent Activity</span>
              <span className="text-xs text-gray-500 ml-2">latest MCP tool invocations</span>
            </div>
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              coming soon
            </span>
          </div>
          <div className="px-4 py-8 text-center text-gray-500 text-xs">
            Activity feed will be available once usage tracking is implemented.
          </div>
        </div>

        {/* right panel */}
        <div className="flex flex-col gap-4">

          {/* sparkline */}
          <div className="border border-neutral-800 rounded overflow-hidden">
            <div className="px-4 py-3 bg-neutral-900 border-b border-neutral-800">
              <span className="font-semibold text-white text-sm">Throughput</span>
            </div>
            <div className="bg-neutral-900 px-4 pt-2 pb-3">
              <Sparkline />
              <div className="flex justify-between font-mono text-xs text-gray-600 mt-1">
                <span>−60s</span><span>now</span>
              </div>
            </div>
          </div>

          {/* system health */}
          <div className="border border-neutral-800 rounded overflow-hidden">
            <div className="px-4 py-3 bg-neutral-900 border-b border-neutral-800">
              <span className="font-semibold text-white text-sm">System Health</span>
            </div>
            <div className="bg-neutral-900 p-4 space-y-3">
              {[
                { label: "Req / Min",    val: String(rpm),  clr: "text-indigo-400" },
                { label: "Avg Latency",  val: `${latMs}ms`, clr: "text-green-400" },
                { label: "Error Rate",   val: `${errPct}%`, clr: "text-amber-400" },
                { label: "Auth Success", val: "99.6%",      clr: "text-green-400" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{item.label}</span>
                  <span className={`font-mono text-sm font-semibold ${item.clr}`}>{item.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* active devs (dynamic — developers with active tokens) */}
          <div className="border border-neutral-800 rounded overflow-hidden">
            <div className="px-4 py-3 bg-neutral-900 border-b border-neutral-800 flex items-center justify-between">
              <span className="font-semibold text-white text-sm">Active Devs</span>
              <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-700 text-green-100">
                {tokens.filter(t => t.isActive).length} active
              </span>
            </div>
            {loading ? (
              <div className="px-4 py-6 text-center text-gray-500 text-xs bg-neutral-900">Loading…</div>
            ) : tokens.filter(t => t.isActive).length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-xs bg-neutral-900">No active developers</div>
            ) : (
              tokens.filter(t => t.isActive).slice(0, 5).map((t, i, arr) => (
                <div key={t.id}
                  className={`flex items-center gap-2.5 px-4 py-2.5 bg-neutral-900 ${i < arr.length - 1 ? "border-b border-neutral-800" : ""} hover:bg-neutral-800/30 transition-colors`}>
                  <div className={`w-6 h-6 rounded bg-gradient-to-br ${t.grad || GRADIENTS[i % GRADIENTS.length]} flex items-center justify-center text-[9px] font-bold flex-shrink-0`}>
                    {t.init || initFromName(t.dev)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-200">{t.dev}</p>
                    <p className="font-mono text-[10px] text-gray-500 truncate">{t.token}</p>
                  </div>
                  <span className="font-mono text-xs font-semibold text-green-400">{t.env}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
